import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, errMsg, fmtIST } from "@/lib/useApi";
import { Sel, Field } from "./QuestionEditor";
import { Loader2 } from "lucide-react";

export default function AICentre() {
  const api = useApi();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [f, setF] = useState({ family: "UGC_NET", pool: "UGC_PAPER1", subject_code: "", section: "", unit: "", difficulty: "medium", count: 5 });
  const [out, setOut] = useState(null);
  const load = () => api.get("/admin/ai/status").then((r) => setSt(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [api]);
  useEffect(() => { api.get(`/subjects?family=${f.family}`).then((r) => setSubjects(r.data)); }, [f.family, api]);

  const ping = async () => {
    setBusy("ping");
    try { const r = await api.post("/admin/ai/ping"); toast.success(`Gemini replied "${r.data.reply}" in ${r.data.latency_ms} ms`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(""); load();
  };
  const draft = async () => {
    setBusy("draft");
    try { setOut((await api.post("/admin/ai/draft-questions", { ...f, subject_code: f.pool === "SUBJECT" ? f.subject_code : null, section: f.section || null })).data); toast.success("Drafts created — review them in the Question bank"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(""); load();
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-ai-centre-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">AI Centre</h1>
        <p className="text-slate-600 mt-2 text-sm">AI never scores, grants access or publishes. It drafts questions (always as drafts) and interprets measured performance statistics into study plans.</p>
      </div>
      {st && <Card className="p-6 border-slate-200 space-y-3" data-testid="ai-status">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={st.configured ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}>{st.configured ? "Configured" : "Not configured"}</Badge>
          <Badge variant="outline">{st.provider} · {st.model}</Badge><Badge variant="outline">{st.key_source}</Badge>
        </div>
        <p className="text-sm text-slate-600">Today: {st.today.ok} ok · {st.today.failed} failed · {st.total_calls} total calls · {st.cached_insight_plans} cached study plans</p>
        <p className="text-xs text-slate-500">{st.limits_note}</p>
        <Button size="sm" variant="outline" onClick={ping} disabled={!!busy} data-testid="ai-ping-btn">{busy === "ping" && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Run live connectivity test</Button>
      </Card>}
      <Card className="p-6 border-slate-200 space-y-4">
        <h2 className="font-display font-semibold">Draft questions with AI</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field l="Family"><Sel value={f.family} onChange={(v) => setF({ ...f, family: v, pool: v === "UGC_NET" ? "UGC_PAPER1" : "CSIR_PARTA", subject_code: "" })} options={[["UGC_NET", "UGC NET"], ["CSIR_NET", "CSIR NET"]]} testid="ai-family" /></Field>
          <Field l="Pool"><Sel value={f.pool} onChange={(v) => setF({ ...f, pool: v })} options={f.family === "UGC_NET" ? [["UGC_PAPER1", "Paper I (shared)"], ["SUBJECT", "Paper II"]] : [["CSIR_PARTA", "Part A (shared)"], ["SUBJECT", "Part B/C"]]} testid="ai-pool" /></Field>
          <Field l="Difficulty"><Sel value={f.difficulty} onChange={(v) => setF({ ...f, difficulty: v })} options={[["easy", "Easy"], ["medium", "Medium"], ["hard", "Hard"]]} /></Field>
          {f.pool === "SUBJECT" && <Field l="Subject"><Sel value={f.subject_code} onChange={(v) => setF({ ...f, subject_code: v })} options={[["", "Select…"], ...subjects.map((s) => [s.code, `${s.code} · ${s.name.slice(0, 50)}`])]} testid="ai-subject" /></Field>}
          {f.pool === "SUBJECT" && f.family === "CSIR_NET" && <Field l="Part"><Sel value={f.section} onChange={(v) => setF({ ...f, section: v })} options={[["", "Select…"], ["B", "B"], ["C", "C"]]} /></Field>}
          <Field l="Unit / topic"><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="ai-unit" /></Field>
          <Field l="How many (1–10)"><Input type="number" min={1} max={10} value={f.count} onChange={(e) => setF({ ...f, count: Number(e.target.value) })} data-testid="ai-count" /></Field>
        </div>
        <Button onClick={draft} disabled={!!busy || !st?.configured} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ai-draft-btn">{busy === "draft" && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Generate drafts</Button>
        {out && <div className="text-sm space-y-1" data-testid="ai-draft-result">
          <p className="font-medium">{out.created.length} draft(s) created · {out.status}</p>
          {out.created.map((c) => <p key={c.id} className="text-slate-600">• {c.stem}{c.duplicates ? ` (${c.duplicates} similar)` : ""}</p>)}
          {out.skipped.map((s, i) => <p key={i} className="text-amber-700">Skipped: {s}</p>)}
        </div>}
      </Card>
      {st && <Card className="p-0 border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Kind</TableHead><TableHead>Result</TableHead><TableHead>Latency</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
          <TableBody>{st.recent.map((u, i) => (
            <TableRow key={i}><TableCell className="text-xs">{fmtIST(u.at)}</TableCell><TableCell className="text-xs">{u.kind}</TableCell>
              <TableCell><Badge variant="outline" className={u.ok ? "text-emerald-700" : "text-red-700"}>{u.ok ? "ok" : "failed"}</Badge></TableCell>
              <TableCell className="text-xs">{u.latency_ms} ms</TableCell><TableCell className="text-xs text-red-600 max-w-xs truncate">{u.error}</TableCell></TableRow>
          ))}</TableBody>
        </Table>
      </Card>}
    </div>
  );
}
